import React from 'react';
import { FaSort, FaSortDown, FaSortUp } from 'react-icons/fa';

interface PageHeaderProps {
  title: string;
  actions?: React.ReactNode;
}

export const PageHeader: React.FC<PageHeaderProps> = ({ title, actions }) => (
  <div className="list-page-header">
    <div className="list-page-title">
      <h2>{title}</h2>
    </div>
    {actions && <div className="list-page-actions">{actions}</div>}
  </div>
);

interface LocalTabsProps<T extends string> {
  tabs: Array<{ id: T; label: string }>;
  active: T;
  onChange: (id: T) => void;
  filters?: React.ReactNode;
}

export function LocalTabs<T extends string>({ tabs, active, onChange, filters }: LocalTabsProps<T>) {
  return (
    <div className="local-tabs">
      <div className="local-tabs-primary">
        {tabs.map(tab => (
          <button
            key={tab.id}
            type="button"
            className={active === tab.id ? 'active' : ''}
            onClick={() => onChange(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {filters && <div className="local-tabs-filters">{filters}</div>}
    </div>
  );
}

interface MetricCardProps {
  label: string;
  value: React.ReactNode;
  detail?: React.ReactNode;
  tone?: 'success' | 'warning' | 'danger' | 'muted';
}

export const MetricCard: React.FC<MetricCardProps> = ({ label, value, detail, tone }) => (
  <div className={`metric-card ${tone || ''}`}>
    <span>{label}</span>
    <strong>{value}</strong>
    {detail && <small>{detail}</small>}
  </div>
);

interface DataTableShellProps {
  children: React.ReactNode;
  className?: string;
}

export const DataTableShell: React.FC<DataTableShellProps> = ({ children, className }) => (
  <div className={`data-table-wrap ${className || ''}`}>{children}</div>
);

type SortDirection = 'asc' | 'desc';

interface SortableTableHeaderProps {
  children: React.ReactNode;
  active: boolean;
  direction: SortDirection;
  onClick: () => void;
  align?: 'right';
  className?: string;
}

export const SortableTableHeader: React.FC<SortableTableHeaderProps> = ({
  children,
  active,
  direction,
  onClick,
  align,
  className,
}) => {
  const Icon = active ? (direction === 'asc' ? FaSortUp : FaSortDown) : FaSort;

  return (
    <th
      className={className}
      aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        className={`table-sort-button ${align === 'right' ? 'right' : ''} ${active ? 'active' : ''}`}
        onClick={onClick}
      >
        <span className="table-sort-label">{children}</span>
        <Icon className="table-sort-icon" aria-hidden="true" focusable="false" />
      </button>
    </th>
  );
};

interface ModalShellProps {
  title: React.ReactNode;
  children: React.ReactNode;
  onClose: () => void;
  footer?: React.ReactNode;
  className?: string;
  headerPrefix?: React.ReactNode;
  headerActions?: React.ReactNode;
}

export const ModalShell: React.FC<ModalShellProps> = ({
  title,
  children,
  onClose,
  footer,
  className,
  headerPrefix,
  headerActions,
}) => (
  <div className="modal-overlay" onClick={onClose}>
    <div className={`modal ${className || ''}`} onClick={(event) => event.stopPropagation()}>
      <div className="modal-header">
        <div className="modal-title-row">
          {headerPrefix}
          <h2>{title}</h2>
        </div>
        <div className="modal-header-actions">
          {headerActions}
          <button className="modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
      </div>
      <div className="modal-content">{children}</div>
      {footer && <div className="modal-footer">{footer}</div>}
    </div>
  </div>
);
